import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
	DataGrid,
	GridColDef,
	GridSortModel,
	GridRenderCellParams,
	GridRowHeightParams,
	useGridApiRef,
	GridCellParams,
	GridPaginationModel,
} from '@mui/x-data-grid';
import DropdownSelect from '../drop-down/DropDownTable';
import ExtendedRow from './ExtendedRow';
import { DataPoint } from '../../utils/models/data-point';
import {
	DataPointTestKitMeasurement,
	ExtractedMeasurement,
} from 'utils/models/test-kit-measurement';
import moment from 'moment';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatNumber } from 'utils/functions/formatNumber';
import convertSiteDataToDataPoints from 'utils/functions/convertSiteDataToDataPoints';
import { updateSingleSitesDataPoints } from 'utils/redux/reducers/allSitesLoadedReducer';
import { useDispatch } from 'react-redux';
import { GridValueGetterParams } from '@mui/x-data-grid';
import CONSTANTS from '../../utils/constants/UIConstants';

const COLUMN_ORDER = [
    "Sample Volume",
    "cATP",
    "tATP RLU",
    "tATP",
    "dATP",
    "AVSS",
    "ABR",
    "BSI",
    "agATP RLU",
    "agATP",
    "cATP (ME/mL)",
    "cATP Log",
    "Notes",
];
const sanitizeField = (name: string) =>
	name.replace(/[\s()./%]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

interface DataRow {
	[key: string]: string | number | any[] | Record<string, number | string | null>;
	date: string;
	outputAlarm: { [key: string]: number };
	allLocations: string;
	allTests: string;
	notes: string;
	id: number;
	history: any;
	outputs: Record<string, number | string | null>;
}

interface CollapsibleTableProps {
	dataPoints?: DataPoint[];
	viewMode: 'latest' | 'all';
	isOwner?: boolean;
	siteId: number;
	isLoading?: boolean;
	tableColumnSettingsAllVOs?: TableColumnSettingsAllVO[];
	userId?: number;
}

const getAlarmColor = (alarm: number) => {
	switch (alarm) {
		case 1: return '#FEE4E2';
		case 2: return '#FEF0C7';
		case 3: return '#D0EFB4';
		default: return 'inherit';
	}
};

const getAlarmType = (alarm: number) => {
	switch (alarm) {
		case 1: return 'red';
		case 2: return 'yellow';
		case 3: return 'green';
		default: return 'default';
	}
};
const DEFAULT_WIDTHS: Record<string, number> = {
	date: 180, operator: 120, allLocations: 200, allTests: 150, notes: 300,
};
const DEFAULT_OUTPUT_WIDTH = 157;

interface RowDetailState {
	isOpen: boolean;
	isLoading: boolean;
	inputData: ExtractedMeasurement[];
	outputData: ExtractedMeasurement[];
	timeStamp: number;
	note: string;
	userId: string;
	originalData?: DataPointTestKitMeasurement;
}

export default function CollapsibleTable({
	viewMode,
	dataPoints,
	isOwner,
	tableColumnSettingsAllVOs,
	siteId,
	isLoading,
	userId,
}: CollapsibleTableProps) {
	const dispatch = useDispatch();

	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
	const [sortedColumn, setSortedColumn] = useState<string | null>('date');
	const [locationFilter, setLocationFilter] = useState<string>('All Locations');
	const [testFilter, setTestFilter] = useState<string>('All Tests');
	const [detailStates, setDetailStates] = useState<Record<number, RowDetailState>>({});
	const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
	const apiRef = useGridApiRef();
	const [filterModel, setFilterModel] = useState<{ items: any[] }>({ items: [] });
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 5000 });
	const HEADER_ICON_ALLOWANCE = 67;
	const ROW_HEIGHT = 44;
	const HEADER_HEIGHT = 49;
	const MAX_GRID_HEIGHT = 593;
	const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
	const SCROLL_TOLERANCE_PX = 2;
	const PERCENT_COLUMN_NAMES = ['ABR', 'BSI'];

	const clampIfPercent = (name: string, value: number | string | null | undefined) => {
		if (value === null || value === undefined || value === '') return value;
		const unit = name.match(/\(([^)]+)\)$/)?.[1];
		const baseName = name.replace(/\s*\([^)]*\)$/, '').trim();
		const isPercentColumn = unit === '%' || PERCENT_COLUMN_NAMES.includes(baseName);
		if (!isPercentColumn) return value;

		const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
		if (Number.isNaN(numericValue)) return value;

		return Math.min(100, Math.max(0, numericValue));
	};

	const measureTextWidth = (() => {
			let canvas: HTMLCanvasElement | null = null;
			return (text: string, font: string = '600 14px "Open Sans", sans-serif') => {
				if (!canvas) canvas = document.createElement('canvas');
				const context = canvas.getContext('2d');
				if (!context) return text.length * 8;
				context.font = font;
				return context.measureText(text).width;
			};
		})();

	const getAutoHeaderWidth = (label: string, minWidth: number) => {
		const textWidth = measureTextWidth(label);
		return Math.max(minWidth, Math.ceil(textWidth) + HEADER_ICON_ALLOWANCE);
	};

	const handleResizeMouseDown = useCallback(
		(field: string, defaultWidth: number) => (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			const startX = e.clientX;
			const startWidth = columnWidths[field] ?? defaultWidth;

			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';

			const onMouseMove = (moveEvent: MouseEvent) => {
				const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX));
				setColumnWidths((prev) => ({ ...prev, [field]: newWidth }));
			};

			const onMouseUp = () => {
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
				window.removeEventListener('mousemove', onMouseMove);
				window.removeEventListener('mouseup', onMouseUp);
			};

			window.addEventListener('mousemove', onMouseMove);
			window.addEventListener('mouseup', onMouseUp);
		},
		[columnWidths]
	);

	const sampleColType = useMemo(() => {
		const result = {} as any;
		tableColumnSettingsAllVOs?.forEach((ele: TableColumnSettingsAllVO) => {
			if (ele.colType === 'Sample') result[ele.colName] = { ...ele };
		});
		return result;
	}, [tableColumnSettingsAllVOs]);

	const outputColumnSettings = useMemo(() => {
		return (
			tableColumnSettingsAllVOs
				?.filter(
					(dp: TableColumnSettingsAllVO) =>
						(dp.colType === 'Input' || dp.colType === 'Output') 
						&& dp.visible
				)
				.sort((a, b) => {
					const aIndex = COLUMN_ORDER.indexOf(a.colName);
					const bIndex = COLUMN_ORDER.indexOf(b.colName);
					return aIndex - bIndex;
				})
			|| []
		);
	}, [tableColumnSettingsAllVOs]);

	const samplePointNames = useMemo(() =>
		Array.from(new Set(dataPoints?.map((dp) => dp.samplePointName))),
	[dataPoints]);

	const testkitNames = useMemo(() =>
		Array.from(new Set(dataPoints?.map((dp) => dp.testkitName))),
	[dataPoints]);

	const filteredDataPoints = useMemo(() =>
		dataPoints
			?.filter((dp) => dp.outputType === 'Output' || dp.outputType === 'Input')
			.filter((dp) => locationFilter === 'All Locations' || dp.samplePointName === locationFilter)
			.filter((dp) => testFilter === 'All Tests' || dp.testkitName === testFilter),
	[dataPoints, locationFilter, testFilter]);

	const visibleOutputColumnSettings = useMemo(() => {
		const hasValue = (value: unknown) =>
			value !== null && value !== undefined && value !== '';

		return outputColumnSettings.filter((setting) =>
			(filteredDataPoints ?? []).some((dp: any) =>
				dp.displayName === setting.colName && hasValue(dp.outputValue)
			)
		);
	}, [filteredDataPoints, outputColumnSettings]);

	const outputColumnDefs = useMemo(() => {
		return visibleOutputColumnSettings.flatMap((ele) => {
			const dpMatches = (filteredDataPoints ?? []).filter((d) => d.displayName === ele.colName);
			const uniqueUnits = new Set(
				dpMatches
					.map((dpMatch) => dpMatch.outputUnit)
					.filter((unit): unit is string => typeof unit === 'string' && unit !== '')
			);

			const buildEntry = (dp: any, unit?: string) => {
				const label = dp?.displayName || ele.colName;
				const rawName = unit ? `${ele.colName} (${unit})` : ele.colName;
				const rawLabel = unit ? `${label} (${unit})` : label;
				return {
					outputName: rawName === 'Risk Score' ? 'RiskScore' : rawName,
					displayLabel: rawLabel === 'Risk Score' ? 'RiskScore' : rawLabel,
				};
			};

			if (uniqueUnits.size > 1) {
				return Array.from(uniqueUnits).map((unit) =>
					buildEntry(dpMatches.find((d) => d.outputUnit === unit), unit)
				);
			}

			return [buildEntry(dpMatches[0], dpMatches[0]?.outputUnit)];
		});
	}, [visibleOutputColumnSettings, filteredDataPoints]);

	const validOutputNames = useMemo(() => outputColumnDefs.map((d) => d.outputName), [outputColumnDefs]);
	const validDisplayNames = useMemo(() => outputColumnDefs.map((d) => d.displayLabel), [outputColumnDefs]);

	const rows: DataRow[] = useMemo(() => {
		if (!filteredDataPoints) return [];

		const dpKey = (dp: any) => dp.outputUnit ? `${dp.displayName} (${dp.outputUnit})` : dp.displayName;
		const groupedData: Record<string, DataRow> = filteredDataPoints.reduce(
			(acc: Record<string, DataRow>, dp: any) => {
				const date = moment(dp.timestamp).format('YYYY/MM/DD - h:mm A');
				if (dp.outputName === 'Risk Score') dp.outputName = 'RiskScore';
				dp.visible = true;

				if (viewMode === 'latest') {
					const key = `${dp.samplePointName}_${dp.testkitName}`;
					if (!acc[key] || moment(dp.timestamp).isAfter(moment(acc[key].timestamp))) {
						acc[key] = {
							date,
							id: dp.id,
							timestamp: dp.timestamp,
							outputAlarm: {},
							outputId: dp.outputId,
							samplePointId: dp.samplePointId,
							allLocations: dp.samplePointName,
							allTests: dp.testkitName,
							testkitId: dp.testkitId,
							notes: dp?.note,
							operator: dp.operator,
							history: [dp?.id],
							outputs: Object.fromEntries(
								validOutputNames.map((name) => [
									name, dpKey(dp) === name ? dp.outputValue : null,
								])
							),
						};
						acc[key].history.push(dp?.id);
					}
					if (moment(dp.timestamp).isSame(moment(acc[key].timestamp)) && acc[key]?.history[0] === dp.id) {
						if (validOutputNames.includes(dpKey(dp)) || validDisplayNames.includes(dp.displayName)) {
							acc[key].outputAlarm[dpKey(dp)] = dp.outputAlarm;
							acc[key].outputs[dpKey(dp)] = dp.outputValue;
						}
					}
				} else {
					const key = `${dp.id}_${dp.samplePointName}_${dp.testkitName}`;
					if (!acc[key]) {
						acc[key] = {
							date,
							outputAlarm: {},
							samplePointId: dp.samplePointId,
							allLocations: dp.samplePointName,
							allTests: dp.testkitName,
							outputId: dp.outputId,
							notes: dp?.note,
							testkitId: dp?.testkitId || "",
							operator: dp.operator,
							history: [dp?.id],
							id: dp.id,
							outputs: Object.fromEntries(
								validOutputNames.map((name) => [
									name, dpKey(dp) === name ? dp.outputValue : null,
								])
							),
						};
					}
					if (validOutputNames.includes(dpKey(dp))) {
						acc[key].outputAlarm[dpKey(dp)] = dp.outputAlarm;
						acc[key].outputs[dpKey(dp)] = dp.outputValue;
					}
				}
				return acc;
			},
			{}
		);

		return Object.values(groupedData);
	}, [filteredDataPoints, validOutputNames, validDisplayNames, viewMode]);

	// Modify the sorting mechanism
	const sorter = useCallback((a: DataRow, b: DataRow) => {
		if (sortedColumn === 'date' || !sortedColumn) {
			const dateA = moment(a.date, 'YYYY/MM/DD - h:mm A');
			const dateB = moment(b.date, 'YYYY/MM/DD - h:mm A');

			return sortDirection === 'asc'
				? dateA.diff(dateB)
				: dateB.diff(dateA);
		}

		const originalOutputName = validOutputNames.find(n => sanitizeField(n) === sortedColumn);
		if (originalOutputName) {
			const aValue = (a.outputs[originalOutputName] ?? 0) as number;
			const bValue = (b.outputs[originalOutputName] ?? 0) as number;
			return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
		}

		const columnAValue = a[sortedColumn];
		const columnBValue = b[sortedColumn];
		if (
			typeof columnAValue === 'string' &&
			typeof columnBValue === 'string'
		) {
			return sortDirection === 'asc'
				? columnAValue.localeCompare(columnBValue)
				: columnBValue.localeCompare(columnAValue);
		}

		return 0;
	}, [sortedColumn, sortDirection, validOutputNames]);

	const gridRows = useMemo(() => {
		const sorted = [...rows].sort(sorter);
		const result: any[] = [];
		sorted.forEach((row) => {
			result.push({
				...row,
				...Object.fromEntries(
					Object.entries(row.outputs).map(([k, v]) => [sanitizeField(k), v])
				),
				_type: 'data',
				_dataRowId: row.id,
				_dataRow: row,
				id: `data-${row.id}`,
			});
			if (detailStates[row.id]?.isOpen) {
				result.push({
					...row,
					...Object.fromEntries(
					Object.entries(row.outputs).map(([outputName, outputValue]) => [sanitizeField(outputName), outputValue])
					),
					_type: 'detail',
					_dataRowId: row.id,
					_dataRow: row,
					id: `detail-${row.id}`,
				});
			}
		});
		return result;
	}, [rows, sorter, detailStates]);

	const dataRowCount = gridRows.filter((r) => r._type === 'data').length;
	const estimatedContentHeight = HEADER_HEIGHT + dataRowCount * ROW_HEIGHT;
	const exceedsMax = estimatedContentHeight > MAX_GRID_HEIGHT;
	
	useEffect(() => {
		setSortDirection('desc'); // Latest date should come first
		setSortedColumn('date'); // Sort based on date
	}, [viewMode]);

	const sortModel: GridSortModel = useMemo(
		() => (sortedColumn ? [{ field: sortedColumn, sort: sortDirection }] : []),
		[sortedColumn, sortDirection]
	);

	const setDetailState = (id: number, patch: Partial<RowDetailState>) => {
		setDetailStates((prev) => ({
			...prev,
			[id]: { ...(prev[id] ?? { isOpen: false, isLoading: false, inputData: [], outputData: [], timeStamp: 0, note: '', userId: '' }), ...patch },
		}));
	};

	const handleToggleRow = useCallback(async (dataRow: DataRow) => {
		const id = dataRow.id;
		const current = detailStates[id];
		if (current?.isOpen) {
			setDetailState(id, { isOpen: false });
			return;
		}
		setDetailState(id, { isOpen: true, isLoading: true });
		try {
			const data = await convertSiteDataToDataPoints(siteId, dataRow) as DataPointTestKitMeasurement;
			const inputData: ExtractedMeasurement[] = [];
			const outputData: ExtractedMeasurement[] = [];
			let timeStamp = 0;
			let note = '';
			let userId = '';
			if (data) {
				timeStamp = data.timeStamp;
				note = data.note ?? '';
				userId = (data as any).username ?? '';
				data.testKitMeasurementResultVOs?.forEach((result: any) => {
					result.testKitMeasurementResultValueVOs?.forEach((valueVO: any) => {
						if (valueVO.type === 'Input' && result.status === 'Active') {
							inputData.push({ name: valueVO.name, value: valueVO.value, alarmType: valueVO.alarmType, id: valueVO.id, type: valueVO.type, displayName: valueVO.displayName });
						} else if (valueVO.type === 'Output' && result.status === 'Active') {
							outputData.push({ name: valueVO.name, value: valueVO.value, alarmType: valueVO.alarmType, id: valueVO.id, type: valueVO.type, displayName: valueVO.displayName });
						}
					});
				});
			}
			setDetailState(id, { isOpen: true, isLoading: false, inputData, outputData, timeStamp, note, userId, originalData: data });
		} catch (e) {
			console.error('Failed to fetch data point from cache:', e);
			setDetailState(id, { isLoading: false });
		}
	}, [detailStates, siteId]);

	const handleUpdateRow = useCallback((updatedRow: any, dataRow: DataRow) => {
		const formattedDate = dayjs(updatedRow.timeStamp).format('YYYY/MM/DD - hh:mm A');
		const newRow: any = { ...dataRow, date: formattedDate, timestamp: updatedRow.timeStamp, notes: updatedRow.note, outputs: { ...dataRow.outputs } };
		updatedRow?.testKitMeasurementResultVOs?.forEach((resultVO: any) => {
			resultVO.testKitMeasurementResultValueVOs.forEach((valueVO: any) => {
				if (newRow.outputs.hasOwnProperty(valueVO.name)) {
					newRow.outputs[valueVO.name] = valueVO.value;
					newRow.outputAlarm[valueVO.name] = valueVO.alarmType;
				}
			});
		});
		dispatch(updateSingleSitesDataPoints({ siteId, ...newRow }));
		setDetailState(dataRow.id, { isOpen: false });
	}, [dispatch, siteId]);

	const activeFilterFields = useMemo(() => {
		const OPERATORS_WITHOUT_VALUE = new Set(['isEmpty', 'isNotEmpty']);
		return new Set(
			filterModel.items
				.filter((item: any) =>
					item.field &&
					(OPERATORS_WITHOUT_VALUE.has(item.operator) ||
						(item.value !== undefined && item.value !== null && item.value !== '' &&
							!(Array.isArray(item.value) && item.value.length === 0)))
				)
				.map((item: any) => item.field)
		);
	}, [filterModel]);
	const columns: GridColDef[] = useMemo(() => {
		const SortArrow = ({ field, offsetLeft, offsetRight }: { field: string; offsetLeft?: boolean; offsetRight?: boolean }) => {
			const isActive = sortedColumn === field;
			if (!isActive) return null;

			const Icon = sortDirection === 'desc' ? ArrowDownwardIcon : ArrowUpwardIcon;
			return (
				<Box
					component="span"
					onClick={(e) => {
						e.stopPropagation();
						setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
					}}
					sx={{
						display: 'flex',
						alignItems: 'center',
						cursor: 'pointer',
						flexShrink: 0,
						...(offsetLeft && { transform: 'translateX(-3px)' }),
						...(offsetRight && { transform: `translateX(3px)` }),
					}}
				>
					<Icon fontSize="small" sx={{ color: '#9BA3A8', flexShrink: 0 }} />
				</Box>
			);
		};

		const ResizeHandle = ({ field, defaultWidth }: { field: string; defaultWidth: number }) => (
			<Box
				className="resize-handle"
				onMouseDown={handleResizeMouseDown(field, defaultWidth)}
				onClick={(e) => e.stopPropagation()}
				sx={{
					position: 'absolute',
					right: -8.5,
					top: 0,
					bottom: 0,
					width: 3,
					transform: 'translateX(50%)',
					cursor: 'col-resize',
					zIndex: 300,
					'&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
				}}
			/>
		);
		const orderedFields = [
			'date',
			...(sampleColType['Operator'].visible ? ['operator'] : []),
			...(sampleColType['Location'].visible ? ['allLocations'] : []),
			...(sampleColType['Test Kit'].visible ? ['allTests'] : []),
			...validOutputNames.filter((_, i) => validDisplayNames[i] !== '').map(sanitizeField),
			...(sampleColType['Notes'].visible ? ['notes'] : []),
		];
		const lastColumnField = orderedFields[orderedFields.length - 1];

		const renderExpandButton = (dataRow: DataRow) => {
			const isOpen = detailStates[dataRow.id]?.isOpen ?? false;
			return (
				<Tooltip title="Details">
					<IconButton
						aria-label="expand row"
						className="expand-test-button"
						size="small"
						onClick={() => handleToggleRow(dataRow)}
					>
						{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</Tooltip>
			);
		};
		const isDetailRow = (params: GridRenderCellParams) => params.row._type === 'detail';

		const detailSpanCell = (params: GridRenderCellParams) => {
			if (params.row._type !== 'detail') return null;
			const dataRow = params.row._dataRow as DataRow;
			const id = dataRow.id;
			const state = detailStates[id];
			if (!state?.isOpen) return null;

			return (
				<Box
					sx={{
						width: state.isLoading ? '100%' : 'max-content',
						minHeight: '100%',
						zIndex: 1,
						overflow: 'visible',
					}}
				>
					{state.isLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
							<CircularProgress />
						</Box>
					) : (
						<ExtendedRow
							onSaveSuccess={(updated: any) => handleUpdateRow(updated, dataRow)}
							inputData={state.inputData}
							outputData={state.outputData}
							timeStamp={state.timeStamp}
							setSelectedRow={() => setDetailState(id, { isOpen: false })}
							note={state.note}
							userId={state.userId}
							row={dataRow}
							originalData={state.originalData}
							isOwner={isOwner}
							siteId={siteId}
						/>
					)}
				</Box>
			);
		};

		const cols: GridColDef[] = [
			{
				field: 'date',
				headerName: 'Date',
				headerClassName: activeFilterFields.has('date') ? 'has-active-filter' : undefined,
				type: 'dateTime',
				width: columnWidths['date'] ?? getAutoHeaderWidth('Date', DEFAULT_WIDTHS['date']),
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				colSpan: (params: GridCellParams) => (params.row._type === 'detail' ? 999 : 1),
				valueGetter: (params: GridValueGetterParams) => {
					const source = params.row._type === 'detail' ? params.row._dataRow : params.row;
					return moment(source?.date, 'YYYY/MM/DD - h:mm A').toDate();
				},
				renderHeader: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
						<Box component="span" sx={{ flexGrow: 1, textAlign: 'left', paddingLeft: '14px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, minWidth: 0, overflow: 'hidden' }}>Date</Box>
						<SortArrow field="date" offsetLeft />
						<ResizeHandle field="date" defaultWidth={columnWidths['date'] ?? getAutoHeaderWidth('Date', DEFAULT_WIDTHS['date'])} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return detailSpanCell(params);
					const [date, time] = String(params.row.date).split(' - ');
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', height: '100%', lineHeight: 1.3, gap: '6px', minWidth: 0 }}>
							<div>{date} - {time}</div>
						</Box>
					);
				},
			},
		];

		if (sampleColType['Operator'].visible) {
			cols.push({
				field: 'operator',
				headerName: 'Operator',
				headerClassName: activeFilterFields.has('Operator') ? 'has-active-filter' : undefined,
				width: columnWidths['operator'] ?? getAutoHeaderWidth('Operator', DEFAULT_WIDTHS['operator']),
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				renderHeader: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
						<Box component="span" sx={{ flexGrow: 1, textAlign: 'left', paddingLeft: '14px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, minWidth: 0, overflow: 'hidden' }}>Operator</Box>
						<SortArrow field="operator" offsetLeft />
						<ResizeHandle field="operator" defaultWidth={columnWidths['operator'] ?? getAutoHeaderWidth('Operator', DEFAULT_WIDTHS['operator'])} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return null;
					if ('operator' !== lastColumnField) return params.value;
					const dataRow = params.row._dataRow as DataRow;
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
							<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, minWidth: 0 }}>
								{params.value}
							</Box>
							{renderExpandButton(dataRow)}
						</Box>
					);
				},
			});
		}

		if (sampleColType['Location'].visible) {
			cols.push({
				field: 'allLocations',
				headerName: 'Location',
				headerClassName: activeFilterFields.has('allLocations') ? 'has-active-filter' : undefined,
				width: columnWidths['allLocations'] ?? getAutoHeaderWidth('Location', DEFAULT_WIDTHS['allLocations']),
				filterable: true,
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				renderHeader: () => (
					<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', minWidth: 0 }}>
						<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textAlign: 'left', minWidth: 0, overflow: 'hidden' }}>
							<Box sx={{ flexGrow: 1, textAlign: 'left', minWidth: 0 }}>
							<DropdownSelect
								value={locationFilter}
								onChange={(location) => setLocationFilter(location)}
								options={['All Locations', ...samplePointNames]}
								addStyle="!font-inter !font-semibold"
							/>
							</Box>
							<SortArrow field="allLocations" offsetLeft />
						</Box>
						<ResizeHandle field="allLocations" defaultWidth={columnWidths['allLocations'] ?? getAutoHeaderWidth('Location', DEFAULT_WIDTHS['allLocations'])} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return null;
					if ('allLocations' !== lastColumnField) return params.value;
					const dataRow = params.row._dataRow as DataRow;
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
							<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, minWidth: 0 }}>
								{params.value}
							</Box>
							{renderExpandButton(dataRow)}
						</Box>
					);
				},
			});
		}

		if (sampleColType['Test Kit'].visible) {
			cols.push({
				field: 'allTests',
				headerName: 'Test Kit',
				headerClassName: activeFilterFields.has('allTests') ? 'has-active-filter' : undefined,
				width: columnWidths['allTests'] ?? getAutoHeaderWidth('Test Kit', DEFAULT_WIDTHS['allTests']),
				filterable: true,
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				renderHeader: () => (
					<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', minWidth: 0 }}>
						<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textAlign: 'left', minWidth: 0, overflow: 'hidden' }}>
							<Box sx={{ flexGrow: 1, textAlign: 'left', minWidth: 0 }}>
							<DropdownSelect
								value={testFilter}
								onChange={(e) => setTestFilter(e)}
								options={['All Tests', ...testkitNames]}
								addStyle="!font-inter !font-semibold"
							/>
							</Box>
							<SortArrow field="allTests" offsetLeft />
						</Box>
						<ResizeHandle field="allTests" defaultWidth={columnWidths['allTests'] ?? getAutoHeaderWidth('Test Kit', DEFAULT_WIDTHS['allTests'])} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return null;
					if ('allTests' !== lastColumnField) return params.value;
					const dataRow = params.row._dataRow as DataRow;
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
							<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, minWidth: 0 }}>
								{params.value}
							</Box>
							{renderExpandButton(dataRow)}
						</Box>
					);
				},
			});
		}

		validOutputNames.forEach((name, index) => {
			const sanitized = sanitizeField(name);
			if (validDisplayNames[index] === '') {
				return;
			}
			const label = validDisplayNames[index] ?? name;
			cols.push({
				field: sanitized,
				headerName: label,
				headerClassName: `output-column-header${activeFilterFields.has(sanitized) ? ' has-active-filter' : ''}`,
				width: columnWidths[sanitized] ?? getAutoHeaderWidth(label, DEFAULT_OUTPUT_WIDTH),
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				align: 'right',
				type: 'number',
				headerAlign: 'left',
				filterable: true,
				cellClassName: 'output-value-cell',
				renderHeader: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', fontWeight: 600, gap: '6px', minWidth: 0 }}>
						<SortArrow field={sanitized} offsetRight/>
						<Box component="span" sx={{ flexGrow: 1, textAlign: 'right', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, minWidth: 0, overflow: 'hidden' }}>{label}</Box>
						<ResizeHandle field={sanitized} defaultWidth={columnWidths[sanitized] ?? getAutoHeaderWidth(label, DEFAULT_OUTPUT_WIDTH)} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return null;
					const dataRow = params.row._dataRow as DataRow;
					const alarm = dataRow.outputAlarm?.[name] ?? 0;
					const alarmType = getAlarmType(alarm);
					const bgColor = getAlarmColor(alarm);
					const formatted = formatNumber(clampIfPercent(name, params.value as number) as any);
					const isLast = sanitized === lastColumnField;
					return (
						<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
							<Box sx={{ position: 'absolute', inset: 0, backgroundColor: bgColor }} />
							<Box
								sx={{
									position: 'relative',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									width: '100%',
									height: '100%',
									minWidth: 0,
									color: alarmType === 'red' ? '#942020' : 'inherit',
									fontFamily: '"Open Sans", sans-serif',
									fontWeight: 'normal',
									whiteSpace: 'nowrap',
									px: 1,
									gap: isLast ? '6px' : 0,
								}}
							>
								{alarmType === 'red' && (
									<img
										src="/assets/icons/Warning.svg"
										alt="Warning"
										style={{ marginBottom: 5, marginRight: 5, flexShrink: 0 }}
									/>
								)}
								<Box sx={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
									{formatted}
								</Box>
								{isLast && renderExpandButton(dataRow)}
							</Box>
						</Box>
					);
				},
			});
		});

		if (sampleColType['Notes'].visible) {
			cols.push({
				field: 'notes',
				headerName: 'Notes',
				headerClassName: activeFilterFields.has('notes') ? 'has-active-filter' : undefined,
				width: columnWidths['notes'] ?? getAutoHeaderWidth('Notes', DEFAULT_WIDTHS['notes']),
				resizable: false,
				disableColumnMenu: false,
				hideable: false,
				renderHeader: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
						<Box component="span" sx={{ flexGrow: 1, textAlign: 'left', paddingLeft: '14px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, minWidth: 0, overflow: 'hidden' }}>Notes</Box>
						<SortArrow field="notes" offsetLeft />
						<ResizeHandle field="notes" defaultWidth={columnWidths['notes'] ?? getAutoHeaderWidth('Notes', DEFAULT_WIDTHS['notes'])} />
					</Box>
				),
				renderCell: (params: any) => {
					if (isDetailRow(params)) return null;
					if ('notes' !== lastColumnField) return params.value;
					const dataRow = params.row._dataRow as DataRow;
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', gap: '6px', minWidth: 0 }}>
							<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, minWidth: 0 }}>
								{params.value}
							</Box>
							{renderExpandButton(dataRow)}
						</Box>
					);
				},
			});
		}
		const lastCol = cols.find((c) => c.field === lastColumnField);
		if (lastCol && !columnWidths[lastColumnField]) {
			const fallbackWidth = lastCol.width as number;
			delete lastCol.width;
			lastCol.flex = 1;
			lastCol.minWidth = fallbackWidth;
		}

		return cols;
	}, [
		sampleColType,
		validOutputNames,
		validDisplayNames,
		locationFilter,
		testFilter,
		samplePointNames,
		testkitNames,
		detailStates,
		handleToggleRow,
		handleUpdateRow,
		isOwner,
		siteId,
		sortedColumn,
		sortDirection,
		columnWidths,
		filterModel,
		activeFilterFields,
	]);
	useEffect(() => {
		const scroller = apiRef.current?.rootElementRef?.current?.querySelector(
			'.MuiDataGrid-virtualScroller'
		) as HTMLElement | null;
		if (!scroller) return;

		const checkScrollState = () => {
			const { scrollWidth, clientWidth } = scroller;
			setHasHorizontalScroll(scrollWidth > clientWidth + SCROLL_TOLERANCE_PX);
		};

		checkScrollState();
		scroller.addEventListener('scroll', checkScrollState, { passive: true });

		const resizeObserver = new ResizeObserver(checkScrollState);
		resizeObserver.observe(scroller);

		return () => {
			scroller.removeEventListener('scroll', checkScrollState);
			resizeObserver.disconnect();
		};
	}, [gridRows, columns, apiRef]);

	return (
		<Box sx={{ width: '100%', ...(exceedsMax ? { height: MAX_GRID_HEIGHT, minHeight: 0 } : {maxHeight: '75vh'}) }}>
			<DataGrid
				apiRef={apiRef}
				disableColumnSelector
				onFilterModelChange={(model) => setFilterModel(model)}
				autoHeight={!exceedsMax}
				columnHeaderHeight={HEADER_HEIGHT}
				rows={gridRows}
				columns={columns}
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[5000]}
				getRowId={(row: any) => row.id}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={(model: any) => {
					if (model.length > 0) {
						setSortedColumn(model[0].field);
						setSortDirection((model[0].sort ?? 'asc') as 'asc' | 'desc');
					} else {
						setSortedColumn('date');
						setSortDirection('desc');
					}
				}}
				getRowHeight={(params: GridRowHeightParams) => {
					if (params.model._type === 'detail') {
						const state = detailStates[params.model._dataRowId as number];
						return state?.isLoading ? 200 : 'auto';
					}
					return ROW_HEIGHT;
				}}
				getEstimatedRowHeight={(params: any) => params.model._type === 'detail' ? 450 : 48}
				getRowClassName={(params: any) => {
					if (params.row._type === 'detail') return 'row-detail-open';
					if (detailStates[params.row._dataRowId as number]?.isOpen) return 'row-header-expanded';
					const idx = gridRows
						.filter((r) => r._type === 'data')
						.findIndex((r) => r._dataRowId === params.row._dataRowId);
					return idx % 2 === 1 ? 'row-zebra-odd' : 'row-zebra-even';
				}}
				disableRowSelectionOnClick
				hideFooter={dataRowCount <= 5000}
				sx={{
					border: 'none',
					'& .MuiDataGrid-virtualScroller': {
						overflowX: hasHorizontalScroll ? 'auto' : 'hidden',
					},
					'& .MuiDataGrid-scrollbarFiller--header': {
						borderRight: 'none',
						borderBottom: '1 solid #e0e0e0 !important',
						backgroundColor: '#FFFFFF',
					},
					'& .MuiDataGrid-scrollbar--horizontal': {
						borderTop: '1px solid #ECEDEF !important',
						borderBottom: 'none',
						borderLeft: 'none',
						borderRight: 'none',
					},
					'--DataGrid-rowBorderColor': 'transparent',

					//cell
					'& .MuiDataGrid-cell:not([data-field="date"])': {
						borderLeft: '1px solid #ECEDEF',
					},
					'& .MuiDataGrid-cell': {
						borderBottom: '1px solid #ECEDEF !important',
						outline: 'none !important',
						display: 'flex',
						alignItems: 'center',
						padding: '4px 8px',
					},
					'& .MuiDataGrid-cell.wrap-text-cell': {
						whiteSpace: 'normal !important',
						wordBreak: 'break-word',
						lineHeight: 1.3,
						py: 0,
					},
					'& .MuiDataGrid-cell.output-value-cell': {
						padding: '0 !important',
					},
					"& .MuiDataGrid-cell[data-field='date'], & .MuiDataGrid-cell[data-field='operator'], & .MuiDataGrid-cell[data-field='allLocations'], & .MuiDataGrid-cell[data-field='allTests'], & .MuiDataGrid-cell[data-field='notes']": {
						padding: '4px 8px 4px 22px !important',
					},
					'& .MuiDataGrid-cell:focus': {
						outline: 'none !important',
					},
					'& .MuiDataGrid-cell:focus-within': {
						outline: 'none !important',
					},

					//column header
					'& .MuiDataGrid-columnHeader': {
						fontWeight: 600,
						boxShadow: 'none',
						overflow: 'visible',
						position: 'relative',
						padding: '0 8px',
					},
					'& .MuiDataGrid-columnHeader:not([data-field="date"])': {
						borderLeft: '1px solid #ECEDEF',
					},
					'& .MuiDataGrid-columnHeader:focus': {
						outline: 'none !important',
					},
					'& .MuiDataGrid-columnHeader:focus-within': {
						outline: 'none !important',
					},
					'& .MuiDataGrid-columnHeaders': {
						overflow: 'visible !important',
						borderBottom: 'none !important',
						boxShadow: 'none',
						zIndex: 1,
						'&::after': {
							content: '""',
							position: 'absolute',
							left: 0,
							right: 0,
							bottom: 0,
							height: '1px',
							backgroundColor: '#ECEDEF',
							zIndex: 1,
							pointerEvents: 'none',
						},
					},
					'& .MuiDataGrid-columnHeadersInner': {
						backgroundColor: '#FFFFFF',
						borderBottom: 'none !important',
						boxShadow: 'none',
					},
					'& .MuiDataGrid-columnHeaderTitleContainer': {
						overflow: 'visible',
						whiteSpace: 'normal !important',
						height: '100%',
						flex: 1,
						minWidth: 0,
					},
					'& .MuiDataGrid-columnHeaderTitleContainerContent': {
						width: '100%',
						height: '100%',
						overflow: 'visible',
						display: 'flex',
						alignItems: 'center',
						order: 1,
					},
					'& .MuiDataGrid-columnHeaderDraggableContainer': {
						overflow: 'visible', 
						minWidth: 0,
					},
					'& .MuiDataGrid-columnSeparator': {
						display: 'none',
					},

					//icons
					'& .MuiDataGrid-sortIcon': { display: 'none' },
					'& .MuiDataGrid-iconButtonContainer': { display: 'none' },
					'& .MuiDataGrid-menuIcon': {
						opacity: 0,
						visibility: 'visible',
						width: '30px !important',
						flexShrink: 0,
						order: -1,
						transition: 'opacity 0.15s ease',
					},
					'& .MuiDataGrid-columnHeader[data-field="date"] .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader[data-field="operator"] .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader[data-field="allLocations"] .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader[data-field="allTests"] .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader[data-field="notes"] .MuiDataGrid-menuIcon': {
						order: 2,
						transform: 'translateX(-5px) !important',
					},
					'& .output-column-header .MuiDataGrid-iconButtonContainer': {
						transform: 'translateX(3px) !important',
					},
					'& .output-column-header .MuiDataGrid-menuIcon': {
						transform: 'translateX(-5px) !important',
					},
					"& .MuiDataGrid-columnHeader[data-field='date'] .resize-handle, & .MuiDataGrid-columnHeader[data-field='operator'] .resize-handle, & .MuiDataGrid-columnHeader[data-field='allLocations'] .resize-handle, & .MuiDataGrid-columnHeader[data-field='allTests'] .resize-handle, & .MuiDataGrid-columnHeader[data-field='notes'] .resize-handle": {
						right: -28.5,
					},
					'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
						opacity: 1,
					},
					'& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-menuIcon': {
						opacity: 1,
					},
					'& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer': {
						flexDirection: 'row',
					},
					'& .MuiDataGrid-columnHeader.has-active-filter .MuiDataGrid-menuIcon': {
						width: 'auto !important',
						display: 'flex !important',
						opacity: '1 !important',
						visibility: 'visible !important',
						color: `${CONSTANTS.PRIMARY_COLOR_BLUE} !important`,
					},
					//rows
					'& .row-detail-open': {
						position: 'relative',
						backgroundColor: '#E6F5FA !important',
						transition: 'none !important',
					},
					'& .row-detail-open:hover': { backgroundColor: '#E6F5FA !important' },
					'& .row-detail-open .MuiDataGrid-cell': {
						border: 'none !important',
						p: '0 !important',
						overflow: 'visible !important',
						backgroundColor: '#E6F5FA !important',
						transition: 'none !important',
					},
					'& .row-detail-open:hover .MuiDataGrid-cell': { backgroundColor: '#E6F5FA !important' },
					'& .row-header-expanded': {
						backgroundColor: '#E6F5FA !important',
					},
					'& .row-header-expanded .MuiDataGrid-cell': {
						backgroundColor: '#E6F5FA !important',
					},
					'& .row-header-expanded:hover, & .row-header-expanded.Mui-hovered': {
						backgroundColor: '#E6F5FA !important',
					},
					'& .row-header-expanded:hover .MuiDataGrid-cell, & .row-header-expanded.Mui-hovered .MuiDataGrid-cell': {
						backgroundColor: '#E6F5FA !important',
					},
					'& .row-zebra-even': { backgroundColor: 'white' },
					'& .row-zebra-odd': { backgroundColor: '#F4F8F9' },
					'& .row-zebra-even:hover, & .row-zebra-even.Mui-hovered': {
						backgroundColor: 'white !important',
					},
					'& .row-zebra-odd:hover, & .row-zebra-odd.Mui-hovered': {
						backgroundColor: '#F4F8F9 !important',
					},
				}}
			/>
		</Box>
	);
}